import {
  query,
  collection,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "@firebase/firestore";
import { Unsubscribe, where } from "firebase/firestore";
import { db } from "../config/firebase-config";
import { IObjective } from "../models/Objective";
import AppStore from "../stores/AppStore";
import AppApi, { apiPathScorecardLevel } from "./AppApi";

export default class DepartmentObjectiveApi {
  private path: string = "";

  constructor(private api: AppApi, private store: AppStore) {}

  private getPath(): string {
    return this.path;
  }

  public setPath(scorecardId: string) {
    this.path = apiPathScorecardLevel(scorecardId, "departmentObjectives");
  }

  async getAll(scorecardId: string, depId?: string) {
    this.setPath(scorecardId);
    const path = this.getPath();
    console.log("📥 Fetching objectives from path:", path);
    if (!path) return;

    this.store.departmentObjective.removeAll();

    const $query = depId
      ? query(collection(db, path), where("department", "==", depId))
      : query(collection(db, path));

    return await new Promise<Unsubscribe>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        $query,
        (querySnapshot) => {
          const items: IObjective[] = [];
          querySnapshot.forEach((doc) => {
            items.push({ id: doc.id, ...doc.data() } as IObjective);
          });
          this.store.departmentObjective.load(items);
          resolve(unsubscribe);
        },
        (error) => reject(error)
      );
    });
  }

  async create(scorecardId: string, item: IObjective) {
    this.setPath(scorecardId);
    const path = this.getPath();
    console.log("📌 Creating objective at path:", path);

    if (!path) return;

    const itemRef = doc(collection(db, path));
    item.id = itemRef.id;

    try {
      await setDoc(itemRef, item, { merge: true });
      console.log("✅ Objective created:", item);
      this.store.departmentObjective.load([item]);
    } catch (error) {
      console.error("❌ Failed to create objective:", error);
    }
  }

  async update(scorecardId: string, item: IObjective) {
    this.setPath(scorecardId);
    const path = this.getPath();
    if (!path) {
      console.warn("No path set for update");
      return;
    }
    if (!item.id) {
      console.warn("No item.id set for update");
      return;
    }

    try {
      // Filter out undefined properties to avoid Firestore errors
      const filtered = Object.fromEntries(
        Object.entries(item).filter(([_, v]) => v !== undefined)
      );

      await updateDoc(doc(db, path, item.id), filtered);
      this.store.departmentObjective.load([item]);
      console.log("✅ Objective updated:", item);
    } catch (error) {
      console.error("❌ Failed to update objective:", error);
    }
  }

  async delete(scorecardId: string, item: IObjective) {
    this.setPath(scorecardId);
    const path = this.getPath();
    if (!path) {
      console.warn("No path set for delete");
      return;
    }
    if (!item.id) {
      console.warn("No item.id set for delete");
      return;
    }

    try {
      await deleteDoc(doc(db, path, item.id));
      this.store.departmentObjective.remove(item.id);
      console.log("✅ Objective deleted:", item.id);
    } catch (error) {
      console.error("❌ Failed to delete objective:", error);
    }
  }
}
